'use client'

import { MutableRefObject } from 'react'

import type { KeenSliderInstance, KeenSliderHooks } from 'keen-slider/react'
import { useEffect, useState, useRef } from 'react'

/**
 * --------------------------------------------------------------------------
 * KeenSliderFade - Plugin for KeenSlider to fade slides in and out
 * --------------------------------------------------------------------------
 * This plugin modifies the opacity and pointer events of slides to create
 * a fade effect during transitions. It handles animations, dragging, and
 * updates to the slider's state.
 **/
export function KeenSliderFade(slider) {
  let activeSlideIndex = 0 // Index of the currently active slide
  let targetSlideIndex = 0 // Index of the target slide during animation
  let withinOne = false // Whether the active and target slides are adjacent

  let isDragging = false // Whether the user is currently dragging the slider

  // Event: Triggered when dragging starts
  slider.on('dragStarted', () => {
    isDragging = true
  })

  // Event: Triggered when dragging ends
  slider.on('dragEnded', () => {
    isDragging = false
  })

  // Event: Triggered when an animation starts
  slider.on('animationStarted', () => {
    if (!slider) {
      return
    }

    // Update active and target slide indices
    activeSlideIndex = slider.track.absToRel(slider.track.details.abs) || 0
    targetSlideIndex = slider.track.absToRel(slider.animator?.targetIdx) || 0
    withinOne = Math.abs(activeSlideIndex - targetSlideIndex) <= 1
  })

  // Event: Triggered when slider details change (e.g., during animations or updates)
  slider.on('detailsChanged', () => {
    if (!slider) {
      return
    }

    const loop = slider?.options?.loop ?? false // Whether the slider loops
    const progress = slider?.track?.details?.progress // Progress of the slider
    const sliderDisabled = slider?.options?.disabled // Whether the slider is disabled

    // Update each slide's opacity, pointer events, and z-index
    slider.track.details.slides.map((slide, index) => {
      // If the slider is disabled, reset opacity and pointer events
      if (sliderDisabled) {
        slider.slides[index].style.opacity = 1
        slider.slides[index].style.pointerEvents = 'auto'
        return
      }

      // Handle opacity and pointer events during dragging or when slides are adjacent
      if (isDragging || withinOne || activeSlideIndex === targetSlideIndex) {
        const beforeFirst = index === 0 && progress < 0 // Dragging before the first slide
        const afterLast = index === slider.slides.length - 1 && progress > 1 // Dragging after the last slide

        if (!loop && (beforeFirst || afterLast)) {
          slider.slides[index].style.opacity = 1
          slider.slides[index].style.pointerEvents = 'auto'
          return
        }

        slider.slides[index].style.opacity = slide.portion
        slider.slides[index].style.pointerEvents = slide.portion > 0 ? 'auto' : 'none'
        slider.slides[index].style.zIndex = slide.portion > 0.5 ? '1' : '0'
        return
      }

      // Handle opacity and pointer events for non-adjacent slides
      if (index == activeSlideIndex || index == targetSlideIndex) {
        const activeSlidePortion = slider.track.details.slides[activeSlideIndex].portion ?? 1
        const targetSlidePortion = slider.track.details.slides[targetSlideIndex].portion ?? 1

        slider.slides[activeSlideIndex].style.opacity = `${activeSlidePortion}`
        slider.slides[activeSlideIndex].style.pointerEvents = 'auto'
        slider.slides[index].style.zIndex = activeSlidePortion > 0.5 ? '1' : '0'

        slider.slides[targetSlideIndex].style.opacity = 1 - activeSlidePortion
        slider.slides[targetSlideIndex].style.pointerEvents = 'auto'
        slider.slides[targetSlideIndex].style.zIndex = targetSlidePortion > 0.5 ? '1' : '0'
      } else {
        slider.slides[index].style.opacity = 0
        slider.slides[index].style.pointerEvents = 'none'
        slider.slides[index].style.zIndex = '0'
      }
    })
  })
}

/**
 * --------------------------------------------------------------------------
 * KeenSliderArrows - Plugin for KeenSlider to add arrow navigation
 * --------------------------------------------------------------------------
 **/

export function KeenSliderArrows({
  sliderInstance,
  disabled,
}: {
  sliderInstance: MutableRefObject<KeenSliderInstance<object, object, KeenSliderHooks>>
  disabled?: boolean
}) {
  const [prevDisabled, setPrevDisabled] = useState(true)
  const [nextDisabled, setNextDisabled] = useState(false)

  useEffect(() => {
    if (!sliderInstance?.current) return

    const currentSlider = sliderInstance.current

    const updateSlideState = () => {
      const details = currentSlider.track.details
      const currentSlide = details.abs ?? 0
      const totalSlides = details.slides.length
      const slidesOption = currentSlider?.options?.slides

      const slidesPerView =
        typeof slidesOption === 'object' &&
        slidesOption !== null &&
        'perView' in slidesOption &&
        Number.isInteger(slidesOption.perView)
          ? Number(slidesOption.perView)
          : 1

      setPrevDisabled(currentSlide === 0)
      setNextDisabled(currentSlide >= totalSlides - slidesPerView)
    }

    currentSlider.on('detailsChanged', updateSlideState)

    // Initialize the state when the component mounts
    updateSlideState()

    // Cleanup the event listener on unmount or when `sliderInstance` changes
    return () => {
      currentSlider.on('detailsChanged', updateSlideState, true) // true = remove
    }
  }, [sliderInstance])

  if (disabled || !sliderInstance || !sliderInstance.current) {
    return null
  }

  const slidesOption = sliderInstance.current.options.slides
  const perView =
    typeof slidesOption === 'object' && slidesOption !== null ? (slidesOption.perView ?? 1) : 1
  const totalSlides = sliderInstance?.current.slides.length

  if (totalSlides <= Number(perView)) {
    return null
  }

  return (
    <div className="keen-slider__arrows mt-m flex justify-end gap-s">
      <KeenSliderArrow
        left
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation()
          sliderInstance.current?.prev()
        }}
        disabled={prevDisabled}
      />

      <KeenSliderArrow
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation()
          sliderInstance.current?.next()
        }}
        disabled={nextDisabled}
      />
    </div>
  )
}

type KeenSliderArrowProps = {
  left?: boolean
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  disabled: boolean
}

function KeenSliderArrow(props: KeenSliderArrowProps) {
  const type = props.left ? 'left' : 'right'
  const direction = type === 'left' ? 'previous' : 'next'

  return (
    <button
      onClick={props.onClick}
      aria-disabled={props.disabled}
      aria-label={`${direction} slide`}
      className={`keen-slider__arrow keen-slider__arrow-${type} flex h-48 w-48 items-center justify-center rounded-full border bg-theme text-fg-title transition-colors duration-150 theme--base-0 ms:h-[40px] ms:w-[40px] [&[aria-disabled="true"]]:pointer-events-none [&[aria-disabled="true"]]:text-fg-title/15 [&[aria-disabled="true"]]:opacity-60`}>
      <svg
        aria-hidden="true"
        className={`h-40 w-40 ms:h-[32px] ms:w-[32px] ${type === 'left' ? 'ml-[-3px]' : 'mr-[-3px]'}`}>
        <use href={`/img/sprite.svg#icon-slider-arrow-${type}`}></use>
      </svg>
    </button>
  )
}

/**
 * --------------------------------------------------------------------------
 * useKeenSliderAutoplay - React hook for KeenSlider autoplay functionality
 * --------------------------------------------------------------------------
 * This hook initializes the autoplay functionality once and manages cleanup,
 * avoiding re-initialization on every render.
 **/
export function useKeenSliderAutoplay(sliderRef: MutableRefObject<KeenSliderInstance | null>) {
  const initialized = useRef(false)

  useEffect(() => {
    // Exit if no slider or autoplay option
    const slider = sliderRef.current
    if (!slider || !slider.options.autoplay || initialized.current) return

    initialized.current = true
    const interval = slider.options.autoplay ?? 1000

    let paused = false
    let isHovered = false
    let timeout: ReturnType<typeof setTimeout>

    function clearNextTimeout() {
      clearTimeout(timeout)
    }

    function nextTimeout() {
      clearNextTimeout()
      if (paused || isHovered) return

      timeout = setTimeout(() => {
        if (paused) return
        slider.next()
      }, interval)
    }

    const onMouseOver = () => {
      isHovered = true
      paused = true
      clearNextTimeout()
    }

    const onMouseOut = () => {
      isHovered = false
      paused = false
      nextTimeout()
    }

    const onAnimationEnded = () => {
      nextTimeout()
    }

    const onUpdated = () => {
      nextTimeout()
    }

    // Set up event listeners
    slider.on('created', () => {
      slider.container.addEventListener('mouseover', onMouseOver)
      slider.container.addEventListener('mouseout', onMouseOut)
      nextTimeout()
    })

    slider.on('dragStarted', clearNextTimeout)
    slider.on('animationStarted', clearNextTimeout)
    slider.on('animationEnded', onAnimationEnded)
    slider.on('updated', onUpdated)

    // Cleanup function
    return () => {
      clearNextTimeout()
      if (slider && slider.container) {
        slider.container.removeEventListener('mouseover', onMouseOver)
        slider.container.removeEventListener('mouseout', onMouseOut)
      }
      if (slider) {
        slider.off('dragStarted', clearNextTimeout)
        slider.off('animationStarted', clearNextTimeout)
        slider.off('animationEnded', onAnimationEnded)
        slider.off('updated', onUpdated)
      }
      initialized.current = false
    }
  }, [sliderRef]) // Only re-run if sliderRef changes
}

/**
 * --------------------------------------------------------------------------
 * KeenSliderAutoplay - Legacy function interface for backward compatibility
 * --------------------------------------------------------------------------
 * This maintains compatibility with the original plugin interface
 * while preventing re-execution on every render.
 **/
export function KeenSliderAutoplay(slider) {
  if (!slider || !slider.options.autoplay) return

  // Create a static map to track initialized sliders
  if (!KeenSliderAutoplay.initialized) {
    KeenSliderAutoplay.initialized = new WeakMap()
  }

  // Only set up autoplay once per slider instance
  if (KeenSliderAutoplay.initialized.get(slider)) {
    return
  }

  KeenSliderAutoplay.initialized.set(slider, true)
  const interval = slider.options.autoplay ?? 1000

  let paused = false
  let isHovered = false
  let timeout: ReturnType<typeof setTimeout>

  function clearNextTimeout() {
    clearTimeout(timeout)
  }

  function nextTimeout() {
    clearNextTimeout()
    if (paused || isHovered) return

    timeout = setTimeout(() => {
      if (paused) return
      slider.next()
    }, interval)
  }

  // Set up event listeners
  slider.on('created', () => {
    const onMouseOver = () => {
      isHovered = true
      paused = true
      clearNextTimeout()
    }

    const onMouseOut = () => {
      isHovered = false
      paused = false
      nextTimeout()
    }

    slider.container.addEventListener('mouseover', onMouseOver)
    slider.container.addEventListener('mouseout', onMouseOut)

    // Store cleanup callback on slider instance
    slider.__autoplayCleanup = () => {
      slider.container.removeEventListener('mouseover', onMouseOver)
      slider.container.removeEventListener('mouseout', onMouseOut)
    }

    nextTimeout()
  })

  slider.on('dragStarted', clearNextTimeout)
  slider.on('animationStarted', clearNextTimeout)
  slider.on('animationEnded', () => {
    nextTimeout()
  })
  slider.on('updated', () => {
    nextTimeout()
  })

  // Clean up previous listeners when slider is destroyed
  slider.on('destroyed', () => {
    clearNextTimeout()
    if (slider.__autoplayCleanup) {
      slider.__autoplayCleanup()
    }
    KeenSliderAutoplay.initialized.delete(slider)
  })
}

// Initialize the static property
KeenSliderAutoplay.initialized = new WeakMap()
